// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ════════════════════════════════════════════════════════════════════════
   Bottom sheets

   Every centred modal in the app becomes a bottom sheet. A dialog floating
   in the middle of a phone screen is a web pattern: it's far from the
   thumb, it leaves dead space above and below, and dismissing it means
   hunting for a small ✕ in a corner.

   A sheet rises from the bottom edge, is reachable one-handed, and is
   dismissed by the gesture people already expect. The markup is unchanged —
   .modal-overlay / .modal-content are simply re-anchored.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .modal-overlay {
  align-items: flex-end;
  padding: 0;
  background: var(--color-overlay);
  /* Fades in rather than snapping — the scrim is what signals the layer
     change before the sheet has finished travelling. */
  animation: m-scrim-in var(--m-dur) var(--m-ease-out);
}

html.m-app .modal-content {
  width: 100%;
  max-width: none;
  /* Not 100vh: leaving a strip of scrim visible at the top is what tells
     you the screen behind is still there and the sheet is dismissible. */
  max-height: 92vh;
  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;
  box-shadow: var(--m-shadow-sheet);
  background: var(--m-surface-card);
  /* Room for the grab handle drawn below, plus the home indicator. */
  padding-top: var(--m-5);
  padding-bottom: var(--m-safe-bottom);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  animation: m-sheet-up var(--m-dur-sheet) var(--m-ease);
}

/* Grab handle. Decorative, so it's a pseudo-element rather than markup —
   the sheet is dismissed by the ✕ or the scrim, and this is the affordance
   that says which direction it came from. */
html.m-app .modal-content::before {
  content: '';
  position: sticky;
  top: 0;
  z-index: 2;
  display: block;
  width: 36px;
  height: 4px;
  margin: 0 auto var(--m-3);
  border-radius: var(--m-r-pill);
  background: var(--m-hairline-strong);
}

/* buttons.css gives every .btn-icon/.modal-close \`font-size: 0\`, which is
   right for a button whose content is an <svg> and wrong for this one --
   .modal-close's content is a text ×. The glyph was being scaled to
   nothing, leaving a 44px circle with nothing in it: the sheet's documented
   close affordance rendered invisible. Put the type size back. */
html.m-app .modal-close {
  top: var(--m-3);
  right: var(--m-3);
  font-size: 1.5rem;
  line-height: 1;
  color: var(--color-text-secondary);
}

@keyframes m-sheet-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}

@keyframes m-scrim-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  html.m-app .modal-content,
  html.m-app .modal-overlay {
    animation: none;
  }
}

/* ── Sheet internals ────────────────────────────────────────────────────── */
html.m-app .m-sheet-header {
  display: flex;
  align-items: center;
  gap: var(--m-3);
  padding: 0 var(--m-gutter) var(--m-3);
  border-bottom: 1px solid var(--m-hairline);
  margin-bottom: var(--m-3);
}

html.m-app .m-sheet-title {
  font-size: var(--m-fs-section);
  font-weight: var(--m-fw-semibold);
  letter-spacing: var(--m-track-title);
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
}

html.m-app .m-sheet-body {
  padding: 0 var(--m-gutter);
}

/* A sheet's confirming action pins to its bottom edge, so it stays put
   while the body scrolls. */
html.m-app .m-sheet-actions {
  position: sticky;
  bottom: 0;
  display: flex;
  gap: var(--m-3);
  padding: var(--m-3) var(--m-gutter);
  padding-bottom: calc(var(--m-3) + var(--m-safe-bottom));
  margin-top: var(--m-4);
  background: var(--m-surface-card);
  border-top: 1px solid var(--m-hairline);
}

html.m-app .m-sheet-actions > * { flex: 1 1; }

/* ── The auth modal ─────────────────────────────────────────────────────
   Login/Register render as full pages elsewhere and are reused inside the
   modal, so their page chrome has to come off in the sheet context. */
html.m-app .modal-content .auth-page {
  min-height: 0;
  padding: 0 var(--m-gutter) var(--m-4);
  background: transparent;
}

html.m-app .modal-auth-tabs {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 2px;
  margin: 0 var(--m-gutter) var(--m-4);
  padding: 3px;
  border: none;
  border-radius: var(--m-r-sm);
  background: var(--m-surface-sunken);
}

html.m-app .modal-auth-tab {
  min-height: 40px;
  border: none;
  border-radius: calc(var(--m-r-sm) - 3px);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--m-fs-body);
  font-weight: var(--m-fw-medium);
}

html.m-app .modal-auth-tab.active {
  background: var(--m-surface-card);
  color: var(--color-text-primary);
  font-weight: var(--m-fw-semibold);
  box-shadow: var(--m-shadow-sm);
}

/* ── Existing slide-in panels ───────────────────────────────────────────
   The cart panel and account sheet already slide; they just need to obey
   the sheet's geometry, safe area and z-order. */
html.m-app :is(.cart-panel, .account-sheet, .as-sheet, .drawer-panel) {
  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;
  max-height: 92vh;
  padding-bottom: var(--m-safe-bottom);
  box-shadow: var(--m-shadow-sheet);
  overscroll-behavior: contain;
}

/* ── Filter drawer ──────────────────────────────────────────────────────
   The storefront's filter sidebar becomes a sheet reached from a button in
   the toolbar, so browsing keeps the full width of the screen. */
html.m-app .m-filter-sheet {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}

html.m-app .m-filter-sheet .m-sheet-body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Body scroll lock is already applied via .overlay-open in App.js; this
   makes it hold on iOS, where overflow:hidden alone doesn't stop the
   rubber band. */
html.m-app body.overlay-open {
  overflow: hidden;
  overscroll-behavior: none;
  touch-action: none;
}

html.m-app body.overlay-open .modal-content,
html.m-app body.overlay-open .cart-panel,
html.m-app body.overlay-open .account-sheet {
  touch-action: auto;
}

/* ════════════════════════════════════════════════════════════════════════
   The login page, wearing the login sheet's design

   Signing in happens through two different components. Tapping Account
   opens .modal-content -- a bottom sheet with a grab handle, a segmented
   Sign In / Create Account control and the form. Landing on any gated URL
   while signed out instead renders .auth-gate-page, a centred desktop card:
   ~330px of dead space above it on a phone, a solid-blue active tab instead
   of the segmented pill, an avatar icon and two lines of prose the sheet
   does not have, and a "Don't have an account?" line duplicating the tabs
   directly above it.

   Same task, same fields, two visibly different screens. This makes the
   page wear the sheet's design: it is anchored to the bottom edge with the
   same radius, handle, shadow and tab treatment, so whichever route the
   customer arrives by, signing in looks like one thing.

   Page-only furniture stays where it earns its place: the checkout gate's
   "or / Continue as Guest" is a real third choice and is left alone.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .auth-gate-page {
  /* was \`center\`, which is what put a third of the screen of nothing above
     the card. The sheet reads from the bottom edge up. */
  align-items: flex-end;
  padding: 0;
  min-height: 0;
  flex: 1 1;
  /* The sheet is white. On the modal route the scrim and the storefront sit
     behind it; here the page does, and .auth-gate-page painted itself white
     too -- so the sheet's edge was carried entirely by its shadow. The
     sunken page colour gives it something to sit on. */
  background: var(--m-surface-page);
}

html.m-app .auth-gate-box {
  position: relative;
  width: 100%;
  max-width: none;
  padding: var(--m-5) var(--m-gutter) var(--m-safe-bottom);
  border: 0;
  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;
  background: var(--m-surface-card);
  box-shadow: var(--m-shadow-sheet);
  text-align: left;
}

/* The same grab handle .modal-content draws, for the same reason: it says
   which edge this came from. */
html.m-app .auth-gate-box::before {
  content: '';
  display: block;
  width: 36px;
  height: 4px;
  margin: 0 auto var(--m-4);
  border-radius: var(--m-r-pill);
  background: var(--m-hairline-strong);
}

/* The sheet opens straight onto the tabs. The icon, the heading and the
   sub-line are the page version's framing, and on a 360px screen they cost
   about 120px above the first field. */
html.m-app :is(.auth-gate-icon-wrap, .auth-gate-sub) {
  display: none;
}

html.m-app .auth-gate-box > h2 {
  display: none;
}

/* ...and the form's own "Sign In" / "Create Account" heading, which the page
   version hides and the sheet shows, comes back to replace it. */
html.m-app .auth-gate-box .auth-container h1 {
  display: block;
  margin: 0 0 var(--m-4);
  text-align: center;
  font-size: var(--m-fs-page-title);
  font-weight: var(--m-fw-bold);
  color: var(--color-text-primary);
}

/* Segmented control, matching .modal-auth-tabs exactly rather than
   approximately -- a solid blue active tab is a different component. */
html.m-app .auth-gate-tabs {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 2px;
  margin: 0 0 var(--m-4);
  padding: 3px;
  border: none;
  border-radius: var(--m-r-sm);
  background: var(--m-surface-sunken);
}

html.m-app .auth-gate-tab {
  min-height: 40px;
  border: none;
  border-radius: calc(var(--m-r-sm) - 3px);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--m-fs-body);
  font-weight: var(--m-fw-medium);
}

html.m-app .auth-gate-tab.active {
  background: var(--m-surface-card);
  color: var(--color-text-primary);
  font-weight: var(--m-fw-semibold);
  box-shadow: var(--m-shadow-sm);
}

/* The box now supplies the gutter, so the form inside it must not add a
   second one. */
html.m-app .auth-gate-box .auth-page {
  min-height: 0;
  padding: 0 0 var(--m-4);
  background: transparent;
}

/* The tabs directly above say the same thing, and say it as a control
   rather than a sentence. */
html.m-app .auth-gate-switch {
  display: none;
}

/* The page version inherits a desktop card's scale-in entrance. A sheet
   arrives from the edge it is anchored to, so it uses the same keyframes
   .modal-content does -- otherwise the two routes to the same form differ
   in motion even once they match in layout. */
html.m-app .auth-gate-box {
  animation: m-sheet-up var(--m-dur-sheet) var(--m-ease);
}

@media (prefers-reduced-motion: reduce) {
  html.m-app .auth-gate-box {
    animation: none;
  }
}

/* Every other sheet puts a title in its top row, leaving the corner free for
   the ✕. This one puts the Sign In / Create Account segmented control there,
   and a 44px button in the corner lands on top of the "Create Account" half
   of it -- the control it overlaps is the one it stops you pressing.
   
   The sheet is still dismissible three ways: tapping the scrim, Escape, and
   the grab handle that says which edge it came from. That is also how the
   design this was matched against behaves -- it shows no ✕ either. */
html.m-app .modal-content-auth .modal-close {
  display: none;
}
`, "",{"version":3,"sources":["webpack://./src/mobile/sheets.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;6EAW6E;;AAE7E;EACE,qBAAqB;EACrB,UAAU;EACV,gCAAgC;EAChC;uDACqD;EACrD,oDAAoD;AACtD;;AAEA;EACE,WAAW;EACX,eAAe;EACf;yEACuE;EACvE,gBAAgB;EAChB,8CAA8C;EAC9C,iCAAiC;EACjC,iCAAiC;EACjC,mEAAmE;EACnE,uBAAuB;EACvB,oCAAoC;EACpC,gBAAgB;EAChB,iCAAiC;EACjC,4BAA4B;EAC5B,sDAAsD;AACxD;;AAEA;;4CAE4C;AAC5C;EACE,WAAW;EACX,gBAAgB;EAChB,MAAM;EACN,UAAU;EACV,cAAc;EACd,WAAW;EACX,WAAW;EACX,yBAAyB;EACzB,8BAA8B;EAC9B,oCAAoC;AACtC;;AAEA;;;;iEAIiE;AACjE;EACE,eAAe;EACf,iBAAiB;EACjB,iBAAiB;EACjB,cAAc;EACd,kCAAkC;AACpC;;AAEA;EACE,OAAO,2BAA2B,EAAE;EACpC,OAAO,wBAAwB,EAAE;AACnC;;AAEA;EACE,OAAO,UAAU,EAAE;EACnB,OAAO,UAAU,EAAE;AACrB;;AAEA;EACE;;IAEE,eAAe;EACjB;AACF;;AAEA,8EAA8E;AAC9E;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,qCAAqC;EACrC,0CAA0C;EAC1C,yBAAyB;AAC3B;;AAEA;EACE,8BAA8B;EAC9B,iCAAiC;EACjC,oCAAoC;EACpC,SAAS;EACT,cAAc;EACd,YAAY;AACd;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;4BAC4B;AAC5B;EACE,gBAAgB;EAChB,SAAS;EACT,aAAa;EACb,eAAe;EACf,mCAAmC;EACnC,uDAAuD;EACvD,sBAAsB;EACtB,iCAAiC;EACjC,uCAAuC;AACzC;;AAEA,kCAAkC,SAAW,EAAE;;AAE/C;;sEAEsE;AACtE;EACE,aAAa;EACb,qCAAqC;EACrC,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,sBAAsB;EACtB,QAAQ;EACR,oCAAoC;EACpC,YAAY;EACZ,YAAY;EACZ,4BAA4B;EAC5B,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,YAAY;EACZ,wCAAwC;EACxC,uBAAuB;EACvB,kCAAkC;EAClC,2BAA2B;EAC3B,+BAA+B;AACjC;;AAEA;EACE,iCAAiC;EACjC,gCAAgC;EAChC,iCAAiC;EACjC,8BAA8B;AAChC;;AAEA;;iDAEiD;AACjD;EACE,8CAA8C;EAC9C,gBAAgB;EAChB,oCAAoC;EACpC,iCAAiC;EACjC,4BAA4B;AAC9B;;AAEA;;iEAEiE;AACjE;EACE,aAAa;EACb,sBAAsB;EACtB,gBAAgB;AAClB;;AAEA;EACE,cAAc;EACd,gBAAgB;EAChB,iCAAiC;AACnC;;AAEA;;iBAEiB;AACjB;EACE,gBAAgB;EAChB,yBAAyB;EACzB,kBAAkB;AACpB;;AAEA;;;EAGE,kBAAkB;AACpB;;AAEA;;;;;;;;;;;;;;;;;;;6EAmB6E;;AAE7E;EACE;yDACuD;EACvD,qBAAqB;EACrB,UAAU;EACV,aAAa;EACb,SAAO;EACP;;;uDAGqD;EACrD,iCAAiC;AACnC;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,eAAe;EACf,wDAAwD;EACxD,SAAS;EACT,8CAA8C;EAC9C,iCAAiC;EACjC,iCAAiC;EACjC,gBAAgB;AAClB;;AAEA;+BAC+B;AAC/B;EACE,WAAW;EACX,cAAc;EACd,WAAW;EACX,WAAW;EACX,yBAAyB;EACzB,8BAA8B;EAC9B,oCAAoC;AACtC;;AAEA;;uCAEuC;AACvC;EACE,aAAa;AACf;;AAEA;EACE,aAAa;AACf;;AAEA;iEACiE;AACjE;EACE,cAAc;EACd,sBAAsB;EACtB,kBAAkB;EAClB,iCAAiC;EACjC,6BAA6B;EAC7B,gCAAgC;AAClC;;AAEA;uEACuE;AACvE;EACE,aAAa;EACb,sBAAsB;EACtB,sBAAsB;EACtB,QAAQ;EACR,sBAAsB;EACtB,YAAY;EACZ,YAAY;EACZ,4BAA4B;EAC5B,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,YAAY;EACZ,wCAAwC;EACxC,uBAAuB;EACvB,kCAAkC;EAClC,2BAA2B;EAC3B,+BAA+B;AACjC;;AAEA;EACE,iCAAiC;EACjC,gCAAgC;EAChC,iCAAiC;EACjC,8BAA8B;AAChC;;AAEA;gBACgB;AAChB;EACE,aAAa;EACb,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;4BAC4B;AAC5B;EACE,aAAa;AACf;;AAEA;;;8CAG8C;AAC9C;EACE,sDAAsD;AACxD;;AAEA;EACE;IACE,eAAe;EACjB;AACF;;AAEA;;;;;;;qEAOqE;AACrE;EACE,aAAa;AACf","sourcesContent":["/* ════════════════════════════════════════════════════════════════════════\n   Bottom sheets\n\n   Every centred modal in the app becomes a bottom sheet. A dialog floating\n   in the middle of a phone screen is a web pattern: it's far from the\n   thumb, it leaves dead space above and below, and dismissing it means\n   hunting for a small ✕ in a corner.\n\n   A sheet rises from the bottom edge, is reachable one-handed, and is\n   dismissed by the gesture people already expect. The markup is unchanged —\n   .modal-overlay / .modal-content are simply re-anchored.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .modal-overlay {\n  align-items: flex-end;\n  padding: 0;\n  background: var(--color-overlay);\n  /* Fades in rather than snapping — the scrim is what signals the layer\n     change before the sheet has finished travelling. */\n  animation: m-scrim-in var(--m-dur) var(--m-ease-out);\n}\n\nhtml.m-app .modal-content {\n  width: 100%;\n  max-width: none;\n  /* Not 100vh: leaving a strip of scrim visible at the top is what tells\n     you the screen behind is still there and the sheet is dismissible. */\n  max-height: 92vh;\n  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;\n  box-shadow: var(--m-shadow-sheet);\n  background: var(--m-surface-card);\n  /* Room for the grab handle drawn below, plus the home indicator. */\n  padding-top: var(--m-5);\n  padding-bottom: var(--m-safe-bottom);\n  overflow-y: auto;\n  -webkit-overflow-scrolling: touch;\n  overscroll-behavior: contain;\n  animation: m-sheet-up var(--m-dur-sheet) var(--m-ease);\n}\n\n/* Grab handle. Decorative, so it's a pseudo-element rather than markup —\n   the sheet is dismissed by the ✕ or the scrim, and this is the affordance\n   that says which direction it came from. */\nhtml.m-app .modal-content::before {\n  content: '';\n  position: sticky;\n  top: 0;\n  z-index: 2;\n  display: block;\n  width: 36px;\n  height: 4px;\n  margin: 0 auto var(--m-3);\n  border-radius: var(--m-r-pill);\n  background: var(--m-hairline-strong);\n}\n\n/* buttons.css gives every .btn-icon/.modal-close `font-size: 0`, which is\n   right for a button whose content is an <svg> and wrong for this one --\n   .modal-close's content is a text ×. The glyph was being scaled to\n   nothing, leaving a 44px circle with nothing in it: the sheet's documented\n   close affordance rendered invisible. Put the type size back. */\nhtml.m-app .modal-close {\n  top: var(--m-3);\n  right: var(--m-3);\n  font-size: 1.5rem;\n  line-height: 1;\n  color: var(--color-text-secondary);\n}\n\n@keyframes m-sheet-up {\n  from { transform: translateY(100%); }\n  to   { transform: translateY(0); }\n}\n\n@keyframes m-scrim-in {\n  from { opacity: 0; }\n  to   { opacity: 1; }\n}\n\n@media (prefers-reduced-motion: reduce) {\n  html.m-app .modal-content,\n  html.m-app .modal-overlay {\n    animation: none;\n  }\n}\n\n/* ── Sheet internals ────────────────────────────────────────────────────── */\nhtml.m-app .m-sheet-header {\n  display: flex;\n  align-items: center;\n  gap: var(--m-3);\n  padding: 0 var(--m-gutter) var(--m-3);\n  border-bottom: 1px solid var(--m-hairline);\n  margin-bottom: var(--m-3);\n}\n\nhtml.m-app .m-sheet-title {\n  font-size: var(--m-fs-section);\n  font-weight: var(--m-fw-semibold);\n  letter-spacing: var(--m-track-title);\n  margin: 0;\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\nhtml.m-app .m-sheet-body {\n  padding: 0 var(--m-gutter);\n}\n\n/* A sheet's confirming action pins to its bottom edge, so it stays put\n   while the body scrolls. */\nhtml.m-app .m-sheet-actions {\n  position: sticky;\n  bottom: 0;\n  display: flex;\n  gap: var(--m-3);\n  padding: var(--m-3) var(--m-gutter);\n  padding-bottom: calc(var(--m-3) + var(--m-safe-bottom));\n  margin-top: var(--m-4);\n  background: var(--m-surface-card);\n  border-top: 1px solid var(--m-hairline);\n}\n\nhtml.m-app .m-sheet-actions > * { flex: 1 1 0; }\n\n/* ── The auth modal ─────────────────────────────────────────────────────\n   Login/Register render as full pages elsewhere and are reused inside the\n   modal, so their page chrome has to come off in the sheet context. */\nhtml.m-app .modal-content .auth-page {\n  min-height: 0;\n  padding: 0 var(--m-gutter) var(--m-4);\n  background: transparent;\n}\n\nhtml.m-app .modal-auth-tabs {\n  display: grid;\n  grid-auto-flow: column;\n  grid-auto-columns: 1fr;\n  gap: 2px;\n  margin: 0 var(--m-gutter) var(--m-4);\n  padding: 3px;\n  border: none;\n  border-radius: var(--m-r-sm);\n  background: var(--m-surface-sunken);\n}\n\nhtml.m-app .modal-auth-tab {\n  min-height: 40px;\n  border: none;\n  border-radius: calc(var(--m-r-sm) - 3px);\n  background: transparent;\n  color: var(--color-text-secondary);\n  font-size: var(--m-fs-body);\n  font-weight: var(--m-fw-medium);\n}\n\nhtml.m-app .modal-auth-tab.active {\n  background: var(--m-surface-card);\n  color: var(--color-text-primary);\n  font-weight: var(--m-fw-semibold);\n  box-shadow: var(--m-shadow-sm);\n}\n\n/* ── Existing slide-in panels ───────────────────────────────────────────\n   The cart panel and account sheet already slide; they just need to obey\n   the sheet's geometry, safe area and z-order. */\nhtml.m-app :is(.cart-panel, .account-sheet, .as-sheet, .drawer-panel) {\n  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;\n  max-height: 92vh;\n  padding-bottom: var(--m-safe-bottom);\n  box-shadow: var(--m-shadow-sheet);\n  overscroll-behavior: contain;\n}\n\n/* ── Filter drawer ──────────────────────────────────────────────────────\n   The storefront's filter sidebar becomes a sheet reached from a button in\n   the toolbar, so browsing keeps the full width of the screen. */\nhtml.m-app .m-filter-sheet {\n  display: flex;\n  flex-direction: column;\n  max-height: 88vh;\n}\n\nhtml.m-app .m-filter-sheet .m-sheet-body {\n  flex: 1 1 auto;\n  overflow-y: auto;\n  -webkit-overflow-scrolling: touch;\n}\n\n/* Body scroll lock is already applied via .overlay-open in App.js; this\n   makes it hold on iOS, where overflow:hidden alone doesn't stop the\n   rubber band. */\nhtml.m-app body.overlay-open {\n  overflow: hidden;\n  overscroll-behavior: none;\n  touch-action: none;\n}\n\nhtml.m-app body.overlay-open .modal-content,\nhtml.m-app body.overlay-open .cart-panel,\nhtml.m-app body.overlay-open .account-sheet {\n  touch-action: auto;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   The login page, wearing the login sheet's design\n\n   Signing in happens through two different components. Tapping Account\n   opens .modal-content -- a bottom sheet with a grab handle, a segmented\n   Sign In / Create Account control and the form. Landing on any gated URL\n   while signed out instead renders .auth-gate-page, a centred desktop card:\n   ~330px of dead space above it on a phone, a solid-blue active tab instead\n   of the segmented pill, an avatar icon and two lines of prose the sheet\n   does not have, and a \"Don't have an account?\" line duplicating the tabs\n   directly above it.\n\n   Same task, same fields, two visibly different screens. This makes the\n   page wear the sheet's design: it is anchored to the bottom edge with the\n   same radius, handle, shadow and tab treatment, so whichever route the\n   customer arrives by, signing in looks like one thing.\n\n   Page-only furniture stays where it earns its place: the checkout gate's\n   \"or / Continue as Guest\" is a real third choice and is left alone.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .auth-gate-page {\n  /* was `center`, which is what put a third of the screen of nothing above\n     the card. The sheet reads from the bottom edge up. */\n  align-items: flex-end;\n  padding: 0;\n  min-height: 0;\n  flex: 1;\n  /* The sheet is white. On the modal route the scrim and the storefront sit\n     behind it; here the page does, and .auth-gate-page painted itself white\n     too -- so the sheet's edge was carried entirely by its shadow. The\n     sunken page colour gives it something to sit on. */\n  background: var(--m-surface-page);\n}\n\nhtml.m-app .auth-gate-box {\n  position: relative;\n  width: 100%;\n  max-width: none;\n  padding: var(--m-5) var(--m-gutter) var(--m-safe-bottom);\n  border: 0;\n  border-radius: var(--m-r-lg) var(--m-r-lg) 0 0;\n  background: var(--m-surface-card);\n  box-shadow: var(--m-shadow-sheet);\n  text-align: left;\n}\n\n/* The same grab handle .modal-content draws, for the same reason: it says\n   which edge this came from. */\nhtml.m-app .auth-gate-box::before {\n  content: '';\n  display: block;\n  width: 36px;\n  height: 4px;\n  margin: 0 auto var(--m-4);\n  border-radius: var(--m-r-pill);\n  background: var(--m-hairline-strong);\n}\n\n/* The sheet opens straight onto the tabs. The icon, the heading and the\n   sub-line are the page version's framing, and on a 360px screen they cost\n   about 120px above the first field. */\nhtml.m-app :is(.auth-gate-icon-wrap, .auth-gate-sub) {\n  display: none;\n}\n\nhtml.m-app .auth-gate-box > h2 {\n  display: none;\n}\n\n/* ...and the form's own \"Sign In\" / \"Create Account\" heading, which the page\n   version hides and the sheet shows, comes back to replace it. */\nhtml.m-app .auth-gate-box .auth-container h1 {\n  display: block;\n  margin: 0 0 var(--m-4);\n  text-align: center;\n  font-size: var(--m-fs-page-title);\n  font-weight: var(--m-fw-bold);\n  color: var(--color-text-primary);\n}\n\n/* Segmented control, matching .modal-auth-tabs exactly rather than\n   approximately -- a solid blue active tab is a different component. */\nhtml.m-app .auth-gate-tabs {\n  display: grid;\n  grid-auto-flow: column;\n  grid-auto-columns: 1fr;\n  gap: 2px;\n  margin: 0 0 var(--m-4);\n  padding: 3px;\n  border: none;\n  border-radius: var(--m-r-sm);\n  background: var(--m-surface-sunken);\n}\n\nhtml.m-app .auth-gate-tab {\n  min-height: 40px;\n  border: none;\n  border-radius: calc(var(--m-r-sm) - 3px);\n  background: transparent;\n  color: var(--color-text-secondary);\n  font-size: var(--m-fs-body);\n  font-weight: var(--m-fw-medium);\n}\n\nhtml.m-app .auth-gate-tab.active {\n  background: var(--m-surface-card);\n  color: var(--color-text-primary);\n  font-weight: var(--m-fw-semibold);\n  box-shadow: var(--m-shadow-sm);\n}\n\n/* The box now supplies the gutter, so the form inside it must not add a\n   second one. */\nhtml.m-app .auth-gate-box .auth-page {\n  min-height: 0;\n  padding: 0 0 var(--m-4);\n  background: transparent;\n}\n\n/* The tabs directly above say the same thing, and say it as a control\n   rather than a sentence. */\nhtml.m-app .auth-gate-switch {\n  display: none;\n}\n\n/* The page version inherits a desktop card's scale-in entrance. A sheet\n   arrives from the edge it is anchored to, so it uses the same keyframes\n   .modal-content does -- otherwise the two routes to the same form differ\n   in motion even once they match in layout. */\nhtml.m-app .auth-gate-box {\n  animation: m-sheet-up var(--m-dur-sheet) var(--m-ease);\n}\n\n@media (prefers-reduced-motion: reduce) {\n  html.m-app .auth-gate-box {\n    animation: none;\n  }\n}\n\n/* Every other sheet puts a title in its top row, leaving the corner free for\n   the ✕. This one puts the Sign In / Create Account segmented control there,\n   and a 44px button in the corner lands on top of the \"Create Account\" half\n   of it -- the control it overlaps is the one it stops you pressing.\n   \n   The sheet is still dismissible three ways: tapping the scrim, Escape, and\n   the grab handle that says which edge it came from. That is also how the\n   design this was matched against behaves -- it shows no ✕ either. */\nhtml.m-app .modal-content-auth .modal-close {\n  display: none;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
